import { Dialog, TextField, Grid, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import isEqual from "lodash/isEqual";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

// import DatePicker from '@mui/lab/DatePicker';
import { INewFile } from "../../containers/Document/Document/interfaces";
import useAuthorization from "../../custom-hooks/useAuthorization/useAuthorization";
import { DocumentTemplateDTO, DocumentTemplateSTO } from "../../models/document-templates";
import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropDown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import FilePickerWithDnD from "../../reusable-components/FilePickerWithDnD/FilePickerWithDnD";

import { DocumentTemplatesApiService } from "../../services/document-templates/document-templates-api.service";
import { LegislationsApiService } from "../../services/legislations/legislations-api.service";

import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import { convertFileToBase64 } from "../../utils/files-utils/convert-file";
import { intlFormatDateShort } from "../../utils/time-utils";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

interface Props {
  item?: DocumentTemplateDTO;
  handleNotifyParent: () => void;
  children?: React.ReactNode;
}

interface State {
  isDialogOpen: boolean;
  loading: boolean;
  documentTemplate: DocumentTemplateSTO;
  isCCDOpen: boolean;
  areFieldsValidated: boolean;
  error: string;
}

const AddEditDocumentTemplate = ({ item, handleNotifyParent, children }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.document_templates.template_history";

  const hasAdminAccess = useAuthorization([AUTHORIZATIONS.CompanyAccountsAccess.LegalEntityAdminAccess]);

  const DEFAULT_ITEM: DocumentTemplateSTO = useMemo(
    () => ({
      id: "",
      name: "",
      isDeleted: false,
      description: "",
      documentDate: new Date(),
      versionComment: "",
      file: null,
      legislation: null,
      fileType: null,
      fileName: null,
    }),
    []
  );

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      documentTemplate: item
        ? ({ ...item, file: null, fileName: null, fileType: null } as DocumentTemplateSTO)
        : DEFAULT_ITEM,
      isCCDOpen: false,
      areFieldsValidated: !!item,
      error: "",
    };
  }, [DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const setOpenDialog = useCallback((value: boolean) => setState((state) => ({ ...state, isDialogOpen: value })), []);

  const handleChangeField = useCallback(
    (field: keyof DocumentTemplateSTO, value: DocumentTemplateSTO[keyof DocumentTemplateSTO]) => {
      setState((state) => ({
        ...state,
        documentTemplate: {
          ...state.documentTemplate,
          [field]: value,
        },
      }));
    },
    [state.documentTemplate]
  );

  //#region save
  // not used, since saving is automatically triggered on document save
  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true, error: "" }));

    try {
      const documentTemplate = {
        ...state.documentTemplate,
        ...(!item && {
          id: uuidv4(),
        }),
      };

      const response = await new DocumentTemplatesApiService().saveItems([documentTemplate]);

      if (!response.error) {
        setState(() => ({
          ...INITIAL_STATE,
        }));
        handleNotifyParent();
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [state.documentTemplate, INITIAL_STATE, handleNotifyParent]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.documentTemplate, INITIAL_STATE.documentTemplate)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return !!state.documentTemplate.legislation && !!state.documentTemplate.name?.trim();
  }, [state.documentTemplate]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.documentTemplate, areFieldsValidated]);

  return (
    <>
      {children ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setOpenDialog(true);
          }}
        >
          {children}
        </div>
      ) : (
        <AddEditButton
          effect={item ? "edit" : "add"}
          title={t(`buttons.${item ? "edit" : "add"}`)}
          onClick={() => setOpenDialog(true)}
        />
      )}
      <Dialog
        open={state.isDialogOpen}
        onClose={(event: any, reason) => {
          if (reason === "backdropClick") {
            event.stopPropagation();
          }
          resetAndCloseDialog();
        }}
        fullWidth
        maxWidth="sm"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle
          title={!item ? t(`${tRP}.title_for_add_component`) : t(`${tRP}.title_for_edit_component`)}
          handleClose={resetAndCloseDialog}
        />
        <LoadingOverlay loading={state.loading}>
          <Grid sx={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%", p: 3 }}>
            <Grid sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <AutocompleteDropDown
                labelKey={t("common_words.legislation")}
                field="legislation"
                serviceApi={new LegislationsApiService()}
                selectedValue={state.documentTemplate.legislation}
                handleChange={handleChangeField}
                required
              />
              <TextField
                required
                label={t(`common_words.name`)}
                value={state.documentTemplate.name}
                onChange={(e) => handleChangeField("name", e.target.value)}
                fullWidth
              />
              <TextField
                label={t(`common_words.description`)}
                value={state.documentTemplate.description}
                onChange={(e) => handleChangeField("description", e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              {item?.documentDate && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    padding: "8px 12px",
                    borderRadius: "16px",
                    // fontSize: "0.875rem",
                    // fontWeight: 500,
                    width: "fit-content",
                  }}
                >
                  Latest template version date : {intlFormatDateShort(item.documentDate)}
                </Typography>
              )}
            </Grid>

            <Grid sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {/* Drag-and-drop file upload and document date picker logic */}
              {/* Custom drag-and-drop file picker and date picker logic */}
              {!item && !!state.documentTemplate && (
                <Grid sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <Typography variant="h6">{t(`${tRP}.add_template`)}</Typography>
                  {/* Drag-and-drop file picker */}
                  <FilePickerWithDnD
                    disabled={!hasAdminAccess}
                    onFileSelect={(file) => {
                      convertFileToBase64({ file, fileName: file?.name ?? "" } as INewFile).then((str) =>
                        handleChangeField("file", str)
                      );
                      handleChangeField("fileType", file?.type);
                      handleChangeField("fileName", file?.name);
                      handleChangeField("documentDate", new Date());
                      handleChangeField("versionComment", "");
                    }}
                  />
                  {/* Show date picker below file picker only if file is selected */}
                  {state.documentTemplate.file && (
                    <>
                      <DatePicker
                        label={t("common_words.document_date")}
                        value={state.documentTemplate.documentDate ?? new Date()}
                        onChange={(date) => {
                          // setSelectedDate(date)
                          handleChangeField("documentDate", date);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            sx={{ mt: 2 }}
                          />
                        )}
                      />
                      <TextField
                        label={t("common_words.version_comment") || "Version Comment"}
                        value={state.documentTemplate?.versionComment ?? ""}
                        onChange={(e) => handleChangeField("versionComment", e.target.value ?? "")}
                        fullWidth
                        sx={{ mt: 2 }}
                      />
                    </>
                  )}
                </Grid>
              )}
              {/* Edit mode: show document date above file picker, and date picker below if file is selected for upload */}
              {!!item && !!state.documentTemplate && (
                <Grid sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <Typography variant="h6">{t(`${tRP}.update_template`)}</Typography>
                  <FilePickerWithDnD
                    disabled={!hasAdminAccess}
                    onFileSelect={(file) => {
                      convertFileToBase64({ file, fileName: file?.name ?? "" } as INewFile).then((str) =>
                        handleChangeField("file", str)
                      );
                      handleChangeField("fileType", file?.type);
                      handleChangeField("fileName", file?.name);
                      handleChangeField("documentDate", new Date());
                      handleChangeField("versionComment", "");
                    }}
                  />
                  {/* Show date picker below file picker only if file is selected */}
                  {state.documentTemplate.file && (
                    <>
                      <DatePicker
                        label={t("common_words.document_date")}
                        value={state.documentTemplate.documentDate ?? new Date()}
                        onChange={(date) => {
                          // setSelectedDate(date)
                          handleChangeField("documentDate", date);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            sx={{ mt: 2 }}
                          />
                        )}
                      />
                      <TextField
                        label={t("common_words.version_comment") || "Version Comment"}
                        value={state.documentTemplate?.versionComment ?? ""}
                        onChange={(e) => handleChangeField("versionComment", e.target.value ?? "")}
                        fullWidth
                        sx={{ mt: 2 }}
                      />
                    </>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>

          <DialogActions
            handleSave={handleSave}
            disabledSaveButton={!areFieldsValidated() || !!state.error}
            handleCancel={resetAndCloseDialog}
            loading={state.loading}
            error={state.error}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default React.memo(AddEditDocumentTemplate);
